package com.qrkontaq.data.http

import com.qrkontaq.core.auth.Session
import com.qrkontaq.core.config.AppConfig
import com.qrkontaq.core.logger.getLogger
import com.qrkontaq.data.dto.RefreshTokenRequestDto
import com.qrkontaq.data.dto.TokenPairDto
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.engine.js.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.auth.*
import io.ktor.client.plugins.auth.providers.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.plugins.logging.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.json.Json

object HttpClientFactory {
    private val logger = getLogger<HttpClientFactory>()

    fun createDefault(): HttpClient {
        return HttpClient(Js) {

            install(ContentNegotiation) {
                json(Json {
                    ignoreUnknownKeys = true
                    isLenient = true
                    encodeDefaults = true
                    prettyPrint = AppConfig.environment == com.qrkontaq.core.config.Environment.DEVELOPMENT
                })
            }

            if (AppConfig.environment == com.qrkontaq.core.config.Environment.DEVELOPMENT) {
                install(Logging) {
                    level = LogLevel.ALL
                    logger = object : io.ktor.client.plugins.logging.Logger {
                        override fun log(message: String) {
                            this@HttpClientFactory.logger.debug(message)
                        }
                    }
                }
            }

            expectSuccess = false
        }
    }

    /**
     * Authenticated client. The [token] seeds the first request; thereafter the Bearer
     * auth plugin reads the live tokens from [Session]. On a 401 it transparently calls
     * POST /v1/auth/refresh with the stored refresh token, rotates the pair back into
     * [Session], and retries the original request once. If refresh fails (or there is no
     * refresh token) the session is cleared so the app can bounce the user to login.
     */
    fun createAuthenticated(token: String): HttpClient {
        return HttpClient(Js) {
            install(ContentNegotiation) {
                json(Json {
                    ignoreUnknownKeys = true
                    isLenient = true
                    encodeDefaults = true
                })
            }

            if (AppConfig.environment == com.qrkontaq.core.config.Environment.DEVELOPMENT) {
                install(Logging) {
                    level = LogLevel.ALL
                    logger = object : io.ktor.client.plugins.logging.Logger {
                        override fun log(message: String) {
                            this@HttpClientFactory.logger.debug(message)
                        }
                    }
                }
            }

            install(Auth) {
                bearer {
                    // Attach the Bearer header preemptively rather than waiting for a 401 challenge.
                    sendWithoutRequest { true }

                    loadTokens {
                        val access = Session.accessToken ?: token
                        BearerTokens(access, Session.refreshToken.orEmpty())
                    }

                    refreshTokens {
                        val refresh = Session.refreshToken
                        if (refresh.isNullOrEmpty()) {
                            Session.clear()
                            return@refreshTokens null
                        }
                        val response =
                            client.post(AppConfig.getApiUrl("v1/auth/refresh")) {
                                // Marks this call so the plugin never tries to refresh it recursively.
                                markAsRefreshTokenRequest()
                                contentType(ContentType.Application.Json)
                                setBody(RefreshTokenRequestDto(refresh))
                            }
                        val pair =
                            if (response.status.isSuccess()) {
                                runCatching { response.body<ApiResponse<TokenPairDto>>().data }.getOrNull()
                            } else {
                                null
                            }
                        if (pair == null) {
                            Session.clear()
                            null
                        } else {
                            Session.accessToken = pair.accessToken
                            Session.refreshToken = pair.refreshToken
                            BearerTokens(pair.accessToken, pair.refreshToken)
                        }
                    }
                }
            }

            defaultRequest {
                header("Content-Type", "application/json")
                header("Accept", "application/json")
            }

            expectSuccess = false
        }
    }
}

fun HttpRequestBuilder.addDefaultHeaders() {
    header("Content-Type", "application/json")
    header("Accept", "application/json")
}

fun HttpRequestBuilder.addAuthHeader(token: String) {
    header("Authorization", "Bearer $token")
}

fun HttpRequestBuilder.setBaseUrl(baseUrl: String) {
    val currentUrl = url.buildString()
    if (!currentUrl.startsWith(baseUrl)) {
        url(baseUrl)
    }
}
